import { useEffect, useMemo } from 'react';
import { Input, Toggle, MultiSelect, KeyValueListEditor } from '../shared';

const toNumber = (raw, fallback) => {
    if (raw === '' || raw === null || raw === undefined) return fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const formatDefault = (val) => {
    if (val === null || val === undefined) return '—';
    if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
    if (typeof val === 'object') {
        const entries = Object.entries(val);
        return entries.length ? entries.map(([k, v]) => `${k}→${Array.isArray(v) ? v.join(',') : v}`).join(' · ') : '—';
    }
    return String(val);
};

const numberHint = (defaultValue) => `default: ${formatDefault(defaultValue)}`;

const hasUserSettings = (value) => {
    if (!value || typeof value !== 'object') return false;
    return Object.keys(value).some((k) => k !== 'enabled');
};

export default function ContextEditingSettings({
    value = {},
    defaults = {},
    availableTools = [],
    onChange,
}) {
    const toolOptions = useMemo(
        () => availableTools.map((t) => (typeof t === 'string' ? { value: t, label: t } : t)),
        [availableTools],
    );

    const toolNameOptions = useMemo(() => toolOptions.map((t) => t.value), [toolOptions]);

    useEffect(() => {
        if (!defaults || Object.keys(defaults).length === 0) return;
        if (hasUserSettings(value)) return;
        onChange({ enabled: value?.enabled ?? true, ...defaults });
    }, [defaults]);

    const update = (patch) => onChange({ ...value, ...patch });

    const pct = value.protect_current_turn || {};
    const pctDefaults = defaults.protect_current_turn || {};
    const pctMode = pct.mode || pctDefaults.mode || 'all';
    const updatePct = (patch) => update({ protect_current_turn: { ...pct, ...patch } });

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
                <Input
                    label="Trigger"
                    hint={numberHint(defaults.trigger)}
                    type="number"
                    min={1000}
                    value={value.trigger ?? ''}
                    placeholder={defaults.trigger != null ? String(defaults.trigger) : undefined}
                    onChange={(e) => update({ trigger: toNumber(e.target.value, undefined) })}
                />
                <Input
                    label="Keep"
                    hint={numberHint(defaults.keep)}
                    type="number"
                    min={0}
                    value={value.keep ?? ''}
                    placeholder={defaults.keep != null ? String(defaults.keep) : undefined}
                    onChange={(e) => update({ keep: toNumber(e.target.value, undefined) })}
                />
                <Input
                    label="Clear at least"
                    hint={numberHint(defaults.clear_at_least)}
                    type="number"
                    min={0}
                    value={value.clear_at_least ?? ''}
                    placeholder={defaults.clear_at_least != null ? String(defaults.clear_at_least) : undefined}
                    onChange={(e) => update({ clear_at_least: toNumber(e.target.value, undefined) })}
                />
            </div>

            <Input
                label="Approximate multiplier"
                hint={numberHint(defaults.approximate_multiplier)}
                type="number"
                step="0.1"
                min={0.1}
                value={value.approximate_multiplier ?? ''}
                placeholder={defaults.approximate_multiplier != null ? String(defaults.approximate_multiplier) : undefined}
                onChange={(e) => update({ approximate_multiplier: toNumber(e.target.value, undefined) })}
            />

            <MultiSelect
                label="Exclude tools"
                hint={`default: ${formatDefault(defaults.exclude_tools)}`}
                options={toolOptions}
                selected={value.exclude_tools || []}
                onChange={(val) => update({ exclude_tools: val.length > 0 ? val : undefined })}
                placeholder="Inherit defaults..."
            />

            <MultiSelect
                label="Offload tools"
                hint={`default: ${formatDefault(defaults.offload_tools)} — results saved to /tool_results/`}
                options={toolOptions}
                selected={value.offload_tools || []}
                onChange={(val) => update({ offload_tools: val.length > 0 ? val : undefined })}
                placeholder="Inherit defaults..."
            />

            <KeyValueListEditor
                label="Exclude tool patterns"
                hint="protect tool results matching path prefix"
                value={value.exclude_tool_patterns || {}}
                defaultValue={defaults.exclude_tool_patterns || {}}
                onChange={(dict) => update({ exclude_tool_patterns: Object.keys(dict).length ? dict : undefined })}
                keyOptions={toolNameOptions}
                keyPlaceholder="tool name (e.g. read_file)"
                valueMode="tags"
                valuePlaceholder="/skills/, /memories/"
                addLabel="Add pattern"
            />

            <KeyValueListEditor
                label="Offload input tools"
                hint="save a specific arg field to /tool_inputs/"
                value={value.offload_input_tools || {}}
                defaultValue={defaults.offload_input_tools || {}}
                onChange={(dict) => update({ offload_input_tools: Object.keys(dict).length ? dict : undefined })}
                keyOptions={toolNameOptions}
                keyPlaceholder="tool name (e.g. visualize_show_chart)"
                valueMode="text"
                valuePlaceholder="field name (e.g. widget_code)"
                addLabel="Add input offload"
            />

            <div className="border-t border-gray-200 pt-3">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-gray-700">Protect current turn</span>
                    <Toggle
                        checked={pct.enabled !== false}
                        onChange={(val) => updatePct({ enabled: val })}
                        size="sm"
                    />
                </div>
                {pct.enabled !== false && (
                    <div className="space-y-2 ml-1">
                        <div className="flex items-center gap-3 text-sm">
                            {[
                                { value: 'all', label: 'All', desc: 'Never clear current turn results' },
                                { value: 'custom', label: 'Custom', desc: 'Override trigger/keep for current turn' },
                            ].map(opt => (
                                <label key={opt.value} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${pctMode === opt.value ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                    <input
                                        type="radio"
                                        name="pct_mode"
                                        value={opt.value}
                                        checked={pctMode === opt.value}
                                        onChange={() => updatePct({ mode: opt.value })}
                                        className="accent-blue-600"
                                    />
                                    <span className="font-medium">{opt.label}</span>
                                    <span className="text-xs text-gray-500">{opt.desc}</span>
                                </label>
                            ))}
                        </div>
                        {pctMode === 'custom' && (
                            <div className="grid grid-cols-2 gap-3 mt-2">
                                <Input
                                    label="Turn trigger"
                                    hint={numberHint(pctDefaults.trigger)}
                                    type="number"
                                    min={1000}
                                    value={pct.trigger ?? ''}
                                    placeholder={pctDefaults.trigger != null ? String(pctDefaults.trigger) : undefined}
                                    onChange={(e) => updatePct({ trigger: toNumber(e.target.value, undefined) })}
                                />
                                <Input
                                    label="Turn keep"
                                    hint={numberHint(pctDefaults.keep)}
                                    type="number"
                                    min={0}
                                    value={pct.keep ?? ''}
                                    placeholder={pctDefaults.keep != null ? String(pctDefaults.keep) : undefined}
                                    onChange={(e) => updatePct({ keep: toNumber(e.target.value, undefined) })}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            <p className="text-xs text-gray-500">
                Leave any field blank to inherit the server defaults from <code className="px-1 py-0.5 bg-gray-100 rounded">CAPABILITY_DEFAULTS</code>.
            </p>
        </div>
    );
}
